import { BlogPost } from '../types';

/**
 * Utility function to fetch blog posts from the public directory.
 * For non-default locales it tries `/data/blogPosts.<locale>.ts` first and
 * falls back to the English file when the localized file is missing or fails.
 */
export const fetchBlogPosts = async (locale?: string): Promise<BlogPost[]> => {
  const suffixes = locale === 'km' ? ['.km', ''] : [''];
  for (const suffix of suffixes) {
    try {
      const response = await fetch(`/data/blogPosts${suffix}.ts`);
      if (!response.ok) {
        if (suffix !== '') {
          continue; // try the English file next
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const text = await response.text();
      const posts = parseBlogPosts(text);
      if (posts.length > 0) {
        return posts;
      }
      if (suffix === '') {
        console.error('Could not parse blogPosts array from file');
      }
    } catch (error) {
      if (suffix === '') {
        console.error('Error loading blog posts:', error);
      }
    }
  }
  return [];
};

/** Extract and evaluate the exported `blogPosts` array from the TS file text. */
function parseBlogPosts(text: string): BlogPost[] {
  // Support optional type annotation after the variable name, e.g.:
  // export const blogPosts: BlogPost[] = [ ... ];
  const arrayMatch = text.match(/export const blogPosts(?:\s*:\s*[^=]+)?\s*=\s*(\[[\s\S]*\]);/);
  if (!arrayMatch || !arrayMatch[1]) {
    return [];
  }
  // Use Function constructor to safely evaluate the array
  const postsArray: BlogPost[] = Function('return ' + arrayMatch[1])();
  const seenSlugs = new Set<string>();
  const uniquePosts = postsArray.filter((post) => {
    if (seenSlugs.has(post.slug)) {
      return false;
    }
    seenSlugs.add(post.slug);
    return true;
  });
  return uniquePosts.sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );
}
